import React, { useEffect, useState } from "react";
import UserService from "../services/UserService";
import { useAuth } from "react-oidc-context";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { debounce } from "@mui/material";
import { GoAFormItem } from "@abgov/react-components";

interface EmailPickerProps {
  userSelectedList: string;
  setUserSelectedList: React.Dispatch<React.SetStateAction<string>>;
  setEmailValidationError: React.Dispatch<React.SetStateAction<string>>;
  className?: string;
}

const EmailPicker: React.FC<EmailPickerProps> = ({
  userSelectedList,
  setUserSelectedList,
  setEmailValidationError,
  className,
}) => {
  
useEffect(() => {
  const updatedEmails = userSelectedList
    ? userSelectedList.split(",").map((e) => ({ label: e, value: e }))
    : [];
  setEmailAddress(updatedEmails);
}, [userSelectedList]);

  const [emailAddressOptions, setEmailAddressOptions] = useState<{ label: string; value: string }[]>([]);
  const [emailAddress, setEmailAddress] = useState<{ label: string; value: string }[]>(
    userSelectedList
      ? userSelectedList.split(",").map((e) => ({ label: e, value: e }))
      : []
  );
  const [rawInputValue, setRawInputValue] = useState("");
  const auth = useAuth();
  const controller = new AbortController();

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateAllEmails = (emails: { label: string; value: string }[]) => {
    if (emails.length > 10) {
      setEmailValidationError("Only up to 10 email addresses are allowed.");
      return false;
    }

    const invalidEmails = emails.filter(email => !validateEmail(email.value));
    if (invalidEmails.length > 0) {
      setEmailValidationError("One or more email addresses are invalid.");
      return false;
    }

    setEmailValidationError(""); // Clear error if all are valid
    return true;
  };

  const updateEmailList = (emails: { label: string; value: string }[]) => {
    setEmailAddress(emails);
    const isValid = validateAllEmails(emails);
    if (isValid) {
      setUserSelectedList(emails.map(email => email.value).join(","));
    }
  };

  const handleDropdownChange = (_: any, value: any) => {
    updateEmailList(value);
  };

  const handleInputChange = (event: any, value: string) => {
    setRawInputValue(value);
    if (event?.type === "change") {
      const lowerCasedInput = value.toLowerCase();
      loadEmailAddressOptions(lowerCasedInput, (result) => {
        let options: any[] = [];
        if (result.length > 0) {
          options = result.map((r) => ({ label: r.email, value: r.email }));
        } else if (validateEmail(value)) {
          options = [{ label: value, value: value }];
        }
        setEmailAddressOptions(options);
      });
    } else if (value.length === 0) {
      setEmailAddressOptions([]);
    }
  };

  const handleBlur = () => {
    const trimmed = rawInputValue.trim();
    if (!trimmed) return;

    const alreadyExists = emailAddress.some(e => e.value === trimmed);
    if (alreadyExists) {
      setRawInputValue("");
      return;
    }

    const newEmails = [...emailAddress, { label: trimmed, value: trimmed }];
    updateEmailList(newEmails);
    setRawInputValue("");
  };

  const handleEnterKey = (emailInput: string) => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;

    const alreadyExists = emailAddress.some(e => e.value === trimmed);
    if (alreadyExists) return;

    const newEmails = [...emailAddress, { label: trimmed, value: trimmed }];
    updateEmailList(newEmails);
    setRawInputValue("");
  };

  const loadEmailAddressOptions = debounce(async (inputValue: string, callback: (data: any[]) => void) => {
    if (!inputValue || inputValue.length < 3) return;
    const response = await UserService.getUsers(
      auth.user?.access_token as string,
      inputValue,
      "userName",
      "asc",
      1,
      100,
      controller
    );
    callback(response.rows);
  }, 500);

  return (
    <GoAFormItem helpText="Add up to 10 valid email addresses">
      <MultiSelectDropdown
        label=""
        fieldName="emailAddress"
        options={emailAddressOptions}
        value={emailAddress}
        onChange={handleDropdownChange}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        onEnter={handleEnterKey}
        className={className}
        placeholder="Enter email address(es)"
      />
    </GoAFormItem>
  );
};

export default EmailPicker;